import moment from 'moment';
import { useState, useMemo } from 'react';
import {
  FiDroplet,
  FiTruck,
  FiActivity,
  FiMap,
  FiTrendingUp,
  FiAlertTriangle,
  FiChevronLeft,
  FiChevronRight,
} from 'react-icons/fi';
import { MdDashboard } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import {
  ResponsiveContainer,
  BarChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Bar,
  Cell,
  PieChart,
  Pie,
  Sector,
  useActiveTooltipDataPoints,
  useIsTooltipActive,
} from 'recharts';

import { LoadingData } from '../../../../components/LoadingData';
import { usePageContext } from '../../../../context/usePage';
import { RankingBarChart } from './RankingBarChart';
import {
  PageContainer,
  Header,
  KpiGrid,
  KpiCard,
  LoadingOverlay,
  ChartContainerWrapper,
  DashboardGrid,
  Span2Chart,
  Span1Chart,
  TableWrapper,
  StyledTable,
  Badge,
  PaginationWrapper,
  PageButton,
  DischargeButton,
  ChartFootnote,
  EmptyRankingState,
} from './styles';
import { IRankingVeiculo } from './types';
import { useDashboardData } from './useDashboardData';
import {
  formatNumber,
  formatName,
  formatCompactNumber,
  STATUS_CONFIG,
} from './utils';

const PIE_COLORS = [
  '#1E4355',
  '#4E8A7C',
  '#6CB4B8',
  '#93C5E1',
  '#A2B87C',
  '#D98A72',
  '#9A80A6',
  '#D8A752',
  '#D48293',
  '#C45A52',
];

const RADIAN = Math.PI / 180;

const renderCustomizedLabelLine = (props: any) => {
  if (props.percent < 0.03) return null;
  if (props.percent >= 0.07) return null;
  const { points } = props;
  if (!points || !points.length) return null;
  const pointsString = points.map((p: any) => `${p.x},${p.y}`).join(' ');

  return (
    <polyline
      points={pointsString}
      fill="none"
      stroke="#1E293B"
      strokeWidth={1}
    />
  );
};

const renderCustomizedLabel = (props: any) => {
  const {
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    percent,
    payload,
    x,
    y,
    textAnchor,
  } = props;

  if (percent < 0.03) return null;

  if (percent >= 0.07) {
    const radius = innerRadius + (outerRadius - innerRadius) * 0.65;
    const innerX = cx + radius * Math.cos(-midAngle * RADIAN);
    const innerY = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text
        x={innerX}
        y={innerY}
        fill="white"
        textAnchor="middle"
        dominantBaseline="central"
        fontSize={12}
        fontWeight={600}
      >
        <tspan x={innerX} dy="-0.5em">
          {payload.PLACA || payload.VEICULO?.split(' ')[0]}
        </tspan>
        <tspan x={innerX} dy="1.2em">
          {`${(percent * 100).toFixed(0)}%`}
        </tspan>
      </text>
    );
  }

  return (
    <text
      x={x}
      y={y}
      fill="#1E293B"
      textAnchor={textAnchor}
      dominantBaseline="central"
      fontSize={12}
      fontWeight={600}
    >
      {`${payload.PLACA || payload.VEICULO?.split(' ')[0]} (${(
        percent * 100
      ).toFixed(0)}%)`}
    </text>
  );
};

const CustomActiveShapePie = (props: any) => {
  const p = useActiveTooltipDataPoints();
  const isAnyPieActive = useIsTooltipActive();
  const isThisPieActive = isAnyPieActive && props.payload === p?.[0];
  const fillOpacity = isAnyPieActive && !isThisPieActive ? 0.5 : 1;

  return (
    <Sector
      {...props}
      fill={PIE_COLORS[props.index % PIE_COLORS.length]}
      fillOpacity={fillOpacity}
      style={{ transition: 'fill-opacity 0.3s ease' }}
    />
  );
};

export const DashboardHome = () => {
  const navigate = useNavigate();
  const { version } = usePageContext();
  const {
    loading,
    resumoData,
    kpis,
    currentPage,
    setCurrentPage,
    totalPages,
    paginatedColetas,
    ordemDescarga,
    setOrdemDescarga,
    loadingFila,
  } = useDashboardData();

  const [ordenacaoVeiculos, setOrdenacaoVeiculos] = useState<'viagens' | 'km'>(
    'viagens',
  );

  const sortedVeiculos = useMemo<IRankingVeiculo[]>(() => {
    if (!resumoData?.ranking_veiculos) return [];
    return [...resumoData.ranking_veiculos].sort((a, b) => {
      if (ordenacaoVeiculos === 'viagens') {
        return (b.QTD_VIAGENS || 0) - (a.QTD_VIAGENS || 0);
      }
      return (b.KM_TOTAL || 0) - (a.KM_TOTAL || 0);
    });
  }, [resumoData?.ranking_veiculos, ordenacaoVeiculos]);

  return (
    <PageContainer>
      <Header>
        <h1>
          <MdDashboard />
          Dashboard
        </h1>
        <p>Gestão de captação.</p>
      </Header>

      {loading || !kpis ? (
        <LoadingOverlay>
          <LoadingData title="Carregando Dashboard" />
        </LoadingOverlay>
      ) : (
        <KpiGrid>
          <KpiCard $delay={0.1} $color="#2563eb">
            <div className="card-content">
              <div className="title">Lt Coletado</div>
              <div className="value">
                {formatNumber(kpis.totalLitros, 0, 'L')}
              </div>
            </div>
            <div className="icon-wrapper">
              <FiDroplet />
            </div>
          </KpiCard>

          <KpiCard $delay={0.2} $color="#8b5cf6">
            <div className="card-content">
              <div className="title">Nº de Viagens</div>
              <div className="value">{formatNumber(kpis.totalViagens)}</div>
            </div>
            <div className="icon-wrapper">
              <FiTruck />
            </div>
          </KpiCard>

          <KpiCard $delay={0.3} $color="#f59e0b">
            <div className="card-content">
              <div className="title">Média por Coleta</div>
              <div className="value">
                {formatNumber(kpis.litragemMedia, 1, 'L')}
              </div>
            </div>
            <div className="icon-wrapper">
              <FiActivity />
            </div>
          </KpiCard>

          <KpiCard
            $delay={0.4}
            $color="#10b981"
            title={`${formatNumber(kpis.kmTotalRodado, 1, 'km')}`}
          >
            <div className="card-content">
              <div className="title">Km Rodado</div>
              <div className="value">
                {formatCompactNumber(kpis.kmTotalRodado)} km
              </div>
            </div>
            <div className="icon-wrapper">
              <FiMap />
            </div>
          </KpiCard>

          <KpiCard $delay={0.5} $color="#ec4899">
            <div className="card-content">
              <div className="title">Média por km</div>
              <div className="value">
                {formatNumber(kpis.litroPorKm, 2, 'L')}
              </div>
            </div>
            <div className="icon-wrapper">
              <FiTrendingUp />
            </div>
          </KpiCard>
        </KpiGrid>
      )}

      {!loading && kpis && kpis.chartData && (
        <DashboardGrid>
          <Span2Chart>
            <h3>Volume Total - Últimos 12 Meses</h3>
            <ResponsiveContainer debounce={100} width="100%" height={300}>
              <BarChart
                data={kpis.chartData}
                margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  vertical={true}
                  stroke="#e2e8f0"
                />
                <XAxis
                  dataKey="name"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#64748b', fontSize: 12, fontWeight: 600 }}
                  dy={10}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#64748b', fontSize: 12, fontWeight: 600 }}
                  tickFormatter={val => formatCompactNumber(val)}
                  dx={-10}
                />
                <Tooltip
                  cursor={{ fill: '#f1f5f9' }}
                  contentStyle={{
                    borderRadius: '12px',
                    border: 'none',
                    boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                    maxWidth: '220px',
                    whiteSpace: 'normal',
                  }}
                  formatter={value => [
                    `${formatNumber(Number(value))} L`,
                    'Volume Total',
                  ]}
                />
                <Bar dataKey="volume" radius={[6, 6, 0, 0]} maxBarSize={50}>
                  {kpis.chartData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={
                        kpis.top3ChartIndexes.includes(index) &&
                        entry.volume > 0
                          ? '#0D9488'
                          : '#5EEAD4'
                      }
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Span2Chart>

          <Span1Chart>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '16px',
              }}
            >
              <h3 style={{ margin: 0 }}>Fila de Descarga</h3>
              <select
                value={ordemDescarga}
                onChange={e =>
                  setOrdemDescarga(e.target.value as 'DESC' | 'ASC')
                }
                style={{
                  padding: '2px 6px',
                  borderRadius: '4px',
                  border: '1px solid #cbd5e1',
                  background: '#f8fafc',
                  color: '#334155',
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  outline: 'none',
                  cursor: 'pointer',
                }}
              >
                <option value="DESC">Mais Recentes</option>
                <option value="ASC">Mais Antigas</option>
              </select>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', maxHeight: '300px' }}>
              {loadingFila && (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100px',
                  }}
                >
                  <span
                    style={{
                      color: '#64748b',
                      fontSize: '0.9rem',
                      fontWeight: 600,
                    }}
                  >
                    Carregando...
                  </span>
                </div>
              )}
              {!loadingFila &&
                resumoData?.fila_descarga &&
                resumoData.fila_descarga.length > 0 && (
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                    {resumoData.fila_descarga.map((item, idx) => {
                      return (
                        <li
                          key={idx}
                          onClick={() =>
                            navigate(
                              `/plataforma/${item.ID_COLETA}/acompanhar${version}`,
                            )
                          }
                          style={{
                            padding: '12px 16px',
                            background: idx % 2 === 0 ? '#f0f9ff' : '#f8fafc',
                            borderRadius: '8px',
                            marginBottom: '8px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '12px',
                            cursor: 'pointer',
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              width: '100%',
                              alignItems: 'flex-start',
                            }}
                          >
                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '2px',
                              }}
                            >
                              <span
                                style={{
                                  fontSize: '0.7rem',
                                  color: '#64748b',
                                  fontWeight: 600,
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.5px',
                                }}
                              >
                                Coleta #{item.ID_COLETA}
                              </span>
                              <strong
                                style={{
                                  fontSize: '0.95rem',
                                  color: '#334155',
                                }}
                              >
                                {item.VEICULO}
                              </strong>
                              <span
                                style={{
                                  fontSize: '0.8rem',
                                  color: '#475569',
                                  marginBottom: '2px',
                                }}
                              >
                                {item.MOTORISTA}
                              </span>
                              <span
                                style={{
                                  fontSize: '0.75rem',
                                  color: '#64748b',
                                }}
                              >
                                Aguardando desde{' '}
                                {item.DATA_SAIDA
                                  ? moment(item.DATA_SAIDA).format('DD/MM/YYYY')
                                  : '--'}{' '}
                                às {item.HORA_SAIDA?.substring(0, 5) || '--:--'}
                              </span>
                            </div>
                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'flex-end',
                                gap: '4px',
                              }}
                            >
                              <strong
                                style={{
                                  fontSize: '0.9rem',
                                  color: '#2563eb',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {formatNumber(Number(item.QTD_COLETADA))} L
                              </strong>
                            </div>
                          </div>
                          <DischargeButton
                            onClick={e => {
                              e.stopPropagation();
                              navigate(
                                `/plataforma/${item.ID_COLETA}${version}`,
                              );
                            }}
                          >
                            Descarregar
                          </DischargeButton>
                        </li>
                      );
                    })}
                  </ul>
                )}
              {!loadingFila &&
                (!resumoData?.fila_descarga ||
                  resumoData.fila_descarga.length === 0) && (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '100%',
                      color: '#94a3b8',
                      fontSize: '0.9rem',
                    }}
                  >
                    Fila vazia
                  </div>
                )}
            </div>
          </Span1Chart>
        </DashboardGrid>
      )}

      {!loading && resumoData && (
        <DashboardGrid>
          <RankingBarChart
            title="Ranking de Linhas"
            data={resumoData.ranking_linhas}
            dataKeyY="DFNOMELINHA"
            yAxisWidth={100}
            barColor="#8b5cf6"
            emptyMessage="Ainda não há coletas registradas neste mês."
            footnote="Volume total coletado por linha até a data atual do mês vigente."
          />

          <RankingBarChart
            title="Ranking de Tanques"
            data={resumoData.ranking_tanques}
            dataKeyY="DFDESCTANQUE"
            yAxisWidth={128}
            barColor="#10b981"
            emptyMessage="Ainda não há coletas registradas neste mês."
            footnote="Volume total coletado por tanque até a data atual do mês vigente."
            tickFormatterY={val => {
              if (!val) return '';
              const parts = String(val).split(' ');
              return parts.slice(0, 2).join(' ');
            }}
          />

          <RankingBarChart
            title="Ranking de Produtores"
            data={resumoData.ranking_produtores}
            dataKeyY="DFNOMEPRODUTOR"
            yAxisWidth={128}
            barColor="#ec4899"
            emptyMessage="Ainda não há coletas registradas neste mês."
            footnote="Volume total de captação por produtor até a data atual do mês vigente."
            tickFormatterY={formatName}
            tooltipContentStyle={{ transform: 'translateX(-110%)' }}
          />
        </DashboardGrid>
      )}

      {!loading && resumoData && resumoData.ranking_veiculos && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            marginBottom: '24px',
          }}
        >
          <ChartContainerWrapper
            style={{
              width: '100%',
              padding: '24px',
              margin: 0,
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                paddingLeft: '8px',
                marginBottom: '16px',
              }}
            >
              <h3 style={{ margin: 0 }}>Ranking de Veículos</h3>
              {sortedVeiculos.length > 0 && (
                <div
                  style={{
                    display: 'flex',
                    gap: '8px',
                    background: '#f1f5f9',
                    padding: '4px',
                    borderRadius: '8px',
                  }}
                >
                  <button
                    onClick={() => setOrdenacaoVeiculos('viagens')}
                    style={{
                      border: 'none',
                      padding: '6px 12px',
                      borderRadius: '6px',
                      fontSize: '0.85rem',
                      fontWeight: 600,
                      cursor: 'pointer',
                      background:
                        ordenacaoVeiculos === 'viagens'
                          ? '#1E4355'
                          : 'transparent',
                      color:
                        ordenacaoVeiculos === 'viagens' ? '#fff' : '#64748b',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    Qtd. Viagens
                  </button>
                  <button
                    onClick={() => setOrdenacaoVeiculos('km')}
                    style={{
                      border: 'none',
                      padding: '6px 12px',
                      borderRadius: '6px',
                      fontSize: '0.85rem',
                      fontWeight: 600,
                      cursor: 'pointer',
                      background:
                        ordenacaoVeiculos === 'km' ? '#1E4355' : 'transparent',
                      color: ordenacaoVeiculos === 'km' ? '#fff' : '#64748b',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    KMs Percorridos
                  </button>
                </div>
              )}
            </div>
            {sortedVeiculos.length === 0 ? (
              <EmptyRankingState>
                Ainda não há coletas registradas neste mês.
              </EmptyRankingState>
            ) : (
              <>
                <div
                  style={{ display: 'flex', gap: '32px', alignItems: 'center' }}
                >
                  <div style={{ flex: 1, height: '360px' }}>
                    <ResponsiveContainer
                      debounce={100}
                      width="100%"
                      height="100%"
                    >
                      <PieChart>
                        <Pie
                          data={sortedVeiculos}
                          dataKey={
                            ordenacaoVeiculos === 'viagens'
                              ? 'QTD_VIAGENS'
                              : 'KM_TOTAL'
                          }
                          nameKey="VEICULO"
                          cx="50%"
                          cy="50%"
                          outerRadius={130}
                          fill="#8884d8"
                          labelLine={renderCustomizedLabelLine as any}
                          label={renderCustomizedLabel}
                          shape={CustomActiveShapePie}
                        />
                        <Tooltip
                          cursor={{ fill: '#f1f5f9' }}
                          separator=": "
                          contentStyle={{
                            borderRadius: '12px',
                            border: 'none',
                            boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                          }}
                          formatter={(value: any, _name: any, props: any) => {
                            const isViagens = ordenacaoVeiculos === 'viagens';
                            const valFormat = isViagens
                              ? Number(value)
                              : formatNumber(Number(value));
                            let labelSuf = 'KMs';
                            if (isViagens) {
                              labelSuf = valFormat === 1 ? 'viagem' : 'viagens';
                            }

                            const total = sortedVeiculos.reduce(
                              (acc: number, curr: IRankingVeiculo) =>
                                acc +
                                (isViagens
                                  ? curr.QTD_VIAGENS || 0
                                  : curr.KM_TOTAL || 0),
                              0,
                            );
                            const percent =
                              total > 0
                                ? ((Number(value) / total) * 100).toFixed(0)
                                : '0';

                            const title = props.payload?.PLACA
                              ? `Placa: ${props.payload.PLACA} | ${valFormat} ${labelSuf}`
                              : `${valFormat} ${labelSuf}`;
                            return [`${percent}%`, title];
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div
                    style={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      height: '360px',
                      overflowY: 'auto',
                      paddingRight: '8px',
                    }}
                  >
                    {sortedVeiculos.map((v: IRankingVeiculo, i: number) => (
                      <div
                        key={i}
                        style={{
                          marginBottom: '8px',
                          padding: '10px 14px',
                          border: '1px solid #e2e8f0',
                          borderRadius: '10px',
                          background: '#f8fafc',
                        }}
                      >
                        <div style={{ marginBottom: '8px' }}>
                          <div
                            style={{
                              fontWeight: 700,
                              color: '#0f172a',
                              display: 'flex',
                              alignItems: 'center',
                              fontSize: '0.8rem',
                            }}
                          >
                            <span
                              style={{
                                display: 'inline-block',
                                width: '12px',
                                height: '12px',
                                borderRadius: '50%',
                                backgroundColor:
                                  PIE_COLORS[i % PIE_COLORS.length],
                                marginRight: '8px',
                              }}
                            ></span>
                            {v.VEICULO}
                          </div>
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            fontSize: '0.75rem',
                            color: '#64748b',
                          }}
                        >
                          {v.PLACA && (
                            <span>
                              <strong>{v.PLACA}</strong>
                            </span>
                          )}
                          <span>
                            <strong>Viagens:</strong> {v.QTD_VIAGENS || 0}
                          </span>
                          <span>
                            <strong>Vol:</strong> {formatNumber(v.VOLUME_TOTAL)}{' '}
                            L
                          </span>
                          <span>
                            <strong>KMs:</strong> {formatNumber(v.KM_TOTAL)} km
                          </span>
                          <span>
                            <strong>Média:</strong>{' '}
                            {formatNumber(v.MEDIA_COLETA)} L/km
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <ChartFootnote>
                  Eficiência da frota no mês vigente (L/km).
                </ChartFootnote>
              </>
            )}
          </ChartContainerWrapper>
        </div>
      )}

      {!loading && resumoData && resumoData.ultimas_coletas && (
        <TableWrapper>
          <div>
            <h3>Últimas Coletas</h3>
            <StyledTable>
              <thead>
                <tr>
                  <th>Coleta</th>
                  <th>Status</th>
                  <th>Veículo</th>
                  <th>Linha</th>
                  <th>Data</th>
                  <th>Qtd. Coletada</th>
                  <th>Anomalia</th>
                  <th>Socorro</th>
                  <th>Tanques Coletados</th>
                  <th>Eficiência</th>
                </tr>
              </thead>
              <tbody>
                {paginatedColetas.map((item, idx) => (
                  <tr
                    key={idx}
                    onClick={() =>
                      navigate(
                        `/plataforma/${item.ID_COLETA}/acompanhar${version}`,
                      )
                    }
                    style={{ cursor: 'pointer' }}
                  >
                    <td>
                      <strong>{item.ID_COLETA}</strong>
                    </td>
                    <td>
                      <Badge
                        $color={STATUS_CONFIG[item.STATUS]?.color || '#64748b'}
                        $hoverable={item.STATUS === 'D'}
                        onClick={e => {
                          if (item.STATUS === 'D') {
                            e.stopPropagation();
                            navigate(`/plataforma/${item.ID_COLETA}${version}`);
                          }
                        }}
                        style={{
                          cursor: item.STATUS === 'D' ? 'pointer' : 'default',
                        }}
                      >
                        {STATUS_CONFIG[item.STATUS]?.label || item.STATUS}
                      </Badge>
                    </td>
                    <td>{item.VEICULO}</td>
                    <td>{item.LINHA}</td>
                    <td>
                      {item.DATA_COLETA
                        ? moment(item.DATA_COLETA).format('DD/MM/YYYY')
                        : '--'}
                    </td>
                    <td>
                      <strong>
                        {formatNumber(Number(item.QTD_COLETADA))} L
                      </strong>
                    </td>
                    <td>
                      {item.ANOMALIA ? (
                        <Badge
                          $color="#ef4444"
                          title={item.TEXTO_ANOMALIA || 'Sem descrição'}
                        >
                          <FiAlertTriangle size={18} />
                        </Badge>
                      ) : (
                        <Badge $color="#10b981">Sem Anomalias</Badge>
                      )}
                    </td>
                    <td>
                      {item.SOCORRO ? (
                        <Badge $color="#f59e0b">Sim</Badge>
                      ) : (
                        <Badge $color="#10b981">Não</Badge>
                      )}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <strong>
                        {item.QTD_TANQUES_COLETADOS ?? 0} /{' '}
                        {item.QTD_TANQUES_PROGRAMADOS ?? 0}
                      </strong>
                    </td>
                    <td>
                      {typeof item.EFICIENCIA === 'number' ? (
                        <div
                          title={`Coletados ${
                            item.QTD_TANQUES_COLETADOS || 0
                          } de ${
                            item.QTD_TANQUES_PROGRAMADOS || 0
                          } tanques programados`}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                            minWidth: '120px',
                          }}
                        >
                          <div
                            style={{
                              flex: 1,
                              background: '#e2e8f0',
                              borderRadius: '4px',
                              height: '8px',
                              overflow: 'hidden',
                            }}
                          >
                            <div
                              style={{
                                width: `${Math.min(item.EFICIENCIA, 100)}%`,
                                background: '#10b981',
                                height: '100%',
                                transition: 'width 0.3s ease',
                              }}
                            ></div>
                          </div>
                          <span
                            style={{
                              fontSize: '0.85rem',
                              fontWeight: 600,
                              color: '#475569',
                              minWidth: '35px',
                              textAlign: 'right',
                            }}
                          >
                            {Math.round(item.EFICIENCIA)}%
                          </span>
                        </div>
                      ) : (
                        '--'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </StyledTable>
          </div>

          {totalPages > 1 && (
            <PaginationWrapper>
              <span>
                Página <strong>{currentPage}</strong> de {totalPages}
              </span>
              <PageButton
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              >
                <FiChevronLeft />
              </PageButton>
              <PageButton
                disabled={currentPage === totalPages}
                onClick={() =>
                  setCurrentPage(prev => Math.min(prev + 1, totalPages))
                }
              >
                <FiChevronRight />
              </PageButton>
            </PaginationWrapper>
          )}
        </TableWrapper>
      )}
    </PageContainer>
  );
};

export default DashboardHome;
